"use client";
import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import ProgressAlertCircular from "@/components/widgets/ProgressAlertCircular";
import { formatIndianNumber } from "@/utils/commonFunctions";
import { allSIPCalculation, calculateSIPValue } from "@/utils/allCalculators";
import ScenarioBottomSheet from "./ScenarioBottomSheet";
import Icon from "@/components/icons/Icons";
import { BucketCardConfig, Plan, RetirementFlowState, RetirementSummaryValues } from "@/type/financialPageTypes";
import { getBucketAllocation, getPreRetirementReturnRate } from "@/utils/financialPageFunctions";
import { useRetirementBuckets } from "../hooks/useRetirementBuckets";
import RetirementBucketGrid, { fmtINR } from "../ui/RetirementBucketGrid";
import ToolTipButton from "../ui/ToolTipButton";

const PLAN_CORPUS: Record<NonNullable<Plan>, number> = {
  "SILVER PLAN": 30_000_000,
  "GOLD PLAN": 50_000_000,
  "PLATINUM PLAN": 100_000_000,
};

const SIP_UPDATION = 500;
const MAX_SIP = 1_00_00_000;
const MAX_RETIREMENT_AGE = 70;
const LIFE_EXPECTANCY_BUFFER = 10;

export default function SummaryPreRetirement({
  selectedPlan,
  planningMode,
  currentAge,
  retirementAge,
  monthlyIncome,
  salaryPercent,
  customAmount,
  onSummaryChangeAction,
}: RetirementFlowState & {
  onSummaryChangeAction?: (values: RetirementSummaryValues) => void;
}) {
  const [adjustedSIP, setAdjustedSIP] = useState<number | null>(null);
  const [showScenarios, setShowScenarios] = useState(false);

  const sipTenure = Math.max(0, retirementAge - currentAge);

  // Future monthly expense at retirement (expenses mode only)
  const futureMonthlyExpense = useMemo(() => {
    if (planningMode !== "expenses") return 0;
    const currentExpense = Number(monthlyIncome) || 0;
    return currentExpense * Math.pow(1.03, sipTenure);
  }, [planningMode, monthlyIncome, sipTenure]);

  // Corpus needed to fund the future monthly expense through post-retirement years
  const expenseCorpus = useMemo(() => {
    if (planningMode !== "expenses") return 0;
    const postRetirementYears = Math.max(0, MAX_RETIREMENT_AGE + LIFE_EXPECTANCY_BUFFER - retirementAge);
    return futureMonthlyExpense * postRetirementYears * 12;
  }, [planningMode, futureMonthlyExpense, retirementAge]);

  // Target corpus based on planning mode
  const targetAmount = useMemo(() => {
    if (selectedPlan) return PLAN_CORPUS[selectedPlan];
    if (planningMode === "expenses") return expenseCorpus;
    return Number(customAmount) || 0;
  }, [selectedPlan, planningMode, expenseCorpus, customAmount]);

  // Core SIP/corpus calculation
  const calculateCorpusSip = useMemo(() => {
    const income = Number(monthlyIncome.replace(/,/g, "")) || 0;
    const sipAmount = (income * salaryPercent) / 100;

    return allSIPCalculation({
      investAmount: sipAmount,
      tenure: sipTenure,
      expectedReturn: getPreRetirementReturnRate(sipTenure),
      targetAmount,
      calclId: planningMode === "salary" ? "futureValueCalc" : "targetValueCalc",
      investType: "Monthly",
    });
  }, [monthlyIncome, salaryPercent, sipTenure, planningMode, targetAmount]);

  const corpus = calculateCorpusSip.futureValue;
  const requiredSIP = calculateCorpusSip.investment;
  const currentSIP = adjustedSIP ?? Math.ceil(requiredSIP / 100) * 100;

  const futureValue = useMemo(
    () =>
      currentSIP > 0 && sipTenure > 0
        ? calculateSIPValue(currentSIP, sipTenure, getPreRetirementReturnRate(sipTenure))
        : 0,
    [currentSIP, sipTenure],
  );

  const totalInvested = currentSIP * sipTenure * 12;
  const wealthMade = Math.max(0, futureValue - totalInvested);

  const goalAchievement = corpus > 0 ? Number(Math.min(100, (futureValue / corpus) * 100).toFixed(5)) : 0;

  const allocation = useMemo(() => getBucketAllocation(corpus), [corpus]);
  const futureBucketsTenure = MAX_RETIREMENT_AGE + LIFE_EXPECTANCY_BUFFER - retirementAge;
  const {
    pensionBucket,
    healthBucket,
    vacationBucket,
    legacyBucket,
    pensionMonthly,
    healthGrows,
    vacationAnnual,
    legacyGrows,
  } = useRetirementBuckets(corpus, futureBucketsTenure, allocation);

  useEffect(() => {
    if (!selectedPlan && !planningMode) return;
    onSummaryChangeAction?.({
      corpus,
      sipValue: currentSIP,
      expectedReturnRate: getPreRetirementReturnRate(sipTenure),
      tenure: sipTenure,
    });
  }, [selectedPlan, planningMode, corpus, currentSIP, sipTenure, onSummaryChangeAction]);

  const tooltipConent =
    "Retirement tenure is estimated based on your retirement age and an assumed life expectancy of 80 years.";

  if (!selectedPlan && !planningMode) {
    return (
      <div className="flex flex-col h-full items-center justify-center p-6 text-center">
        <p className="text-[#d70000] font-semibold">Complete the previous steps to view your retirement summary.</p>
      </div>
    );
  }

  const bucketCards: BucketCardConfig[] = [
    {
      title: "Pension Bucket",
      dotColor: "#FDE006",
      value: pensionBucket,
      secondaryValue: pensionMonthly,
      prefix: <Icon.rightArrow />,
      suffix: "/ M",
    },
    {
      title: "Health Reserve",
      dotColor: "#79FD06",
      value: healthBucket,
      secondaryValue: healthGrows,
      prefix: "Grows to",
      suffix: "",
    },
    {
      title: "Vacation",
      dotColor: "#04BCFF",
      value: vacationBucket,
      secondaryValue: vacationAnnual,
      prefix: <Icon.rightArrow />,
      suffix: "/ Yr",
    },
    {
      title: "Legacy",
      dotColor: "#FF9304",
      value: legacyBucket,
      secondaryValue: legacyGrows,
      prefix: "Grows to",
      suffix: "",
    },
  ];

  return (
    <div className="space-y-2">
      <div
        className={[
          "flex flex-col sm:flex-row items-stretch gap-4 sm:gap-5 md:gap-6",
          showScenarios ? "overflow-hidden" : "overflow-x-auto scrollbar-none sm:overflow-hidden",
        ].join(" ")}
      >
        {/* Left: dark goal card */}
        <div className="flex flex-col rounded-2xl md:rounded-6xl p-4 xl:p-5 text-white shrink-0 w-full sm:max-w-90 md:max-w-96 bg-cover bg-center summary-step-financial">
          <p className="text-sm font-medium text-white/60 mb-1 sm:mb-1.5">Estimated Goal Amount</p>

          <div className="text-xl xl:text-2xl font-bold text-white mb-1 sm:mb-2">{fmtINR(corpus)}</div>

          <p className="text-2xs font-medium text-white/60 mb-2.5">
            (To be achieved with {getPreRetirementReturnRate(sipTenure)}% CAGR over {sipTenure} years)
          </p>

          <div className="pt-2 xs:px-2 xs:py-3 sm:p-4 xs:rounded-xl sm:rounded-2xl border-t border-dashed xs:border-solid xs:border border-white/25 xs:bg-white/15 xs:backdrop-blur-3xl">
            <div className="text-2xs sm:text-sm sm:leading-5 leading-1.2 font-medium text-white text-center mb-3 tracking-[-3%]">
              <span>Here&apos;s your Corpus utilisation</span>{" "}
              <span className="inline-flex items-center gap-1">
                ({futureBucketsTenure} Years)
                <ToolTipButton
                  content={tooltipConent}
                  side="right"
                  className="w-44! max-w-none! whitespace-normal!"
                  toolTipIconColor="sm:hover:text-white/60"
                />
              </span>
            </div>

            <RetirementBucketGrid buckets={bucketCards} />
          </div>

          <button
            onClick={() => setShowScenarios(true)}
            className="mt-4 sm:mt-auto text-center text-sm font-medium text-white/60 underline underline-offset-2 transition hover:text-white/90"
          >
            Understand Your Retirement Scenario
          </button>
        </div>

        {/* Right: Summary + SIP */}
        <div className="flex flex-col gap-5 flex-1">
          {/* Summary table */}
          <div className="text-sm border border-gray-200 rounded-xl sm:rounded-2xl md:rounded-6xl p-3 sm:p-4 xl:px-6">
            <div className="flex justify-between items-center pb-2 mb-2 sm:pb-3 sm:mb-3 border-b border-dashed border-[#D9D9D9]">
              <span className="font-medium text-black">Summary</span>
              <span className="text-black/70">Value</span>
            </div>
            <div className="space-y-2 sm:space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-black/70">Monthly SIP Required</span>
                <span className="font-bold text-primaryBlue">₹{requiredSIP.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-black/70">Years to Retirement</span>
                <span className="font-medium text-black">{sipTenure} Years</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-black/70">Total Invested</span>
                <span className="font-medium text-black">{fmtINR(totalInvested)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-black/70">Wealth Made</span>
                <span className="font-semibold text-[#158700]">{fmtINR(wealthMade)}</span>
              </div>
            </div>
          </div>

          {/* Suggested Monthly SIP */}
          <div className="flex-1 bg-[#F0F4F9] rounded-xl sm:rounded-2xl md:rounded-6xl p-3 sm:p-4 xl:p-5">
            <p className="text-center text-sm font-medium text-black/70 mb-3">Suggested Monthly SIP</p>

            <div className="flex items-center justify-between gap-2 bg-[#E3EAF4] rounded-xl p-2 pl-4 mb-4">
              <span className="text-sm font-medium text-primaryBlack">Goal Achievement:</span>
              <ProgressAlertCircular score={goalAchievement} size={48} strokeWidth={4}>
                <span className="text-xs font-medium text-primaryBlack">{goalAchievement.toFixed(0)}%</span>
              </ProgressAlertCircular>
            </div>

            <div className="flex items-center justify-center gap-3 xl:gap-4 mb-4">
              <button
                onClick={() => setAdjustedSIP((prev) => Math.max(500, (prev ?? Math.ceil(requiredSIP / 100) * 100) - SIP_UPDATION))}
                className="w-8 h-8 shrink-0 rounded-full border-2 border-primaryBlue text-black flex items-center justify-center text-lg font-bold sm:hover:bg-primaryBlue/10 transition"
                aria-label="Decrease SIP"
              >
                <Icon.expandableIcon isOpen={true} className="w-3 h-3" />
              </button>
              <div className="flex items-baseline gap-1 text-2.5xl font-semibold text-primaryBlue">
                ₹
                <input
                  type="text"
                  inputMode="numeric"
                  value={formatIndianNumber(currentSIP)}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9]/g, "");
                    setAdjustedSIP(raw ? Math.min(Number(raw), MAX_SIP) : 0);
                  }}
                  aria-label="Monthly SIP amount"
                  className="w-32 bg-transparent text-2.5xl font-semibold text-primaryBlue outline-hidden border-b border-dashed border-primaryBlue/40 focus:border-primaryBlue text-center"
                />
              </div>
              <button
                onClick={() => setAdjustedSIP((prev) => Math.min(MAX_SIP, (prev ?? Math.ceil(requiredSIP / 100) * 100) + SIP_UPDATION))}
                className="w-8 h-8 shrink-0 rounded-full border-2 border-primaryBlue text-black flex items-center justify-center text-lg font-bold sm:hover:bg-primaryBlue/10 transition"
                aria-label="Increase SIP"
              >
                <Icon.expandableIcon className="w-3 h-3" />
              </button>
            </div>

            {!currentSIP || currentSIP <= 0 || currentSIP % 100 !== 0 ? (
              <p className="text-red-500 text-xs font-medium text-center">
                Enter a valid amount. The SIP amount must be a multiple of 100.
              </p>
            ) : (
              <p className="text-center text-xs font-medium text-primaryBlack/60">
                {goalAchievement >= 100
                  ? "Outstanding! You’re set for a comfortable and legacy-rich retirement"
                  : `Your Goal is short by ${fmtINR(corpus - wealthMade)}. Increase your SIP to meet your Goal by Retirement.`}
              </p>
            )}
          </div>
        </div>

        <AnimatePresence>
          {showScenarios && <ScenarioBottomSheet key="scenario-bottom-sheet" onClose={() => setShowScenarios(false)} />}
        </AnimatePresence>
      </div>

      <p className="text-[10px] text-black/30 text-center pb-2">
        <b>Disclaimer:</b> Calculations and projected fund returns are based on the historical performance of mutual
        funds. Actual investment returns depend on market conditions and may vary over time.
      </p>
    </div>
  );
}
